import { CardNotification, Text } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { useTranslation } from "react-i18next";
import type { ValidationPresentation } from "../../types";
import css from "./validationMessageList.module.scss";

export type ValidationMessageListVariant = "inline" | "notification";

interface ValidationMessageListProps {
  messages: ValidationPresentation[];
  /** `inline` hangs the messages under a table row, `notification` wraps them in an error card. */
  variant?: ValidationMessageListVariant;
  title?: string;
  className?: string;
}

/**
 * Renders validation messages that cannot be anchored to a single group input.
 *
 * Both final steps of the wizard (league configuration and initial groups) share it so
 * subject and package level errors are always visible, whichever step submits the mutation.
 */
const ValidationMessageList = ({
  messages,
  variant = "inline",
  title,
  className,
}: ValidationMessageListProps) => {
  const { t } = useTranslation();

  if (!messages.length) return null;

  const list = (
    <ul className={css.validationMessageList}>
      {messages.map(message => (
        <li className={css.validationMessageList_item} key={`${message.reason}-${message.message}`}>
          <Icon
            filled
            className={css.validationMessageList_icon}
            name="circle-cross"
            size="sm"
            color="error-50"
          />
          <Text type="sm" color="error-50">
            {message.message}
          </Text>
        </li>
      ))}
    </ul>
  );

  if (variant === "notification") {
    return (
      <CardNotification
        className={className}
        state="error"
        outlined
        title={title ?? t("package-wizard.common.validation-error-title")}
      >
        {list}
      </CardNotification>
    );
  }

  return <div className={className}>{list}</div>;
};

export default ValidationMessageList;
